<template>
  <v-dialog :model-value="show" persistent max-width="500">
    <v-card>
      <v-card-title class="text-h6">{{ title }}</v-card-title>

      <v-alert
        v-if="isEdit && hasSentOffers"
        type="warning"
        variant="tonal"
        class="mx-4 mb-2 flex-shrink-0"
        density="compact"
      >
        A dátum, a helység, a létszám, az elhelyezés vagy az ellátás módosítása
        érvényteleníti a már megkapott ajánlatokat.
      </v-alert>

      <div class="dialog-fields">
        <v-card-text>
          <v-text-field
            v-model="area"
            label="Helység"
            variant="outlined"
            :rules="[rules.required]"
          />
        </v-card-text>
        <v-card-text>
          <v-select
            v-model="selectedCostCenter"
            :items="costCenters"
            item-title="name"
            item-value="id"
            label="Költséghely"
            variant="outlined"
            :rules="[rules.required]"
          />
        </v-card-text>
        <v-card-text>
          <Datepicker
            v-model="from"
            placeholder="Érkezés"
            :enable-time-picker="false"
            :format="'yyyy-MM-dd'"
            auto-apply
          />
          <div v-if="fromError" class="text-error text-caption mt-1">
            {{ fromError }}
          </div>
        </v-card-text>
        <v-card-text>
          <Datepicker
            v-model="to"
            placeholder="Távozás"
            :enable-time-picker="false"
            :format="'yyyy-MM-dd'"
            auto-apply
          />
          <div v-if="toError" class="text-error text-caption mt-1">
            {{ toError }}
          </div>
        </v-card-text>
        <v-card-text>
          <v-text-field
            v-model="numberOfPeople"
            label="Létszám"
            variant="outlined"
            type="number"
            :rules="[rules.required, rules.minOne]"
          />
        </v-card-text>
        <v-card-text>
          <v-select
            v-model="placementType"
            :items="placementTypeOptions"
            item-title="label"
            item-value="value"
            label="Elhelyezés"
            variant="outlined"
            :rules="[rules.required]"
          />
        </v-card-text>
        <v-card-text>
          <v-text-field
            v-model="contactName"
            label="Kapcsolattartó neve"
            variant="outlined"
            :rules="[rules.required]"
          />
        </v-card-text>
        <v-card-text>
          <v-text-field
            v-model="contactPhone"
            label="Kapcsolattartó telefonszáma"
            variant="outlined"
            :rules="[rules.required]"
          />
        </v-card-text>
        <v-card-text>
          <v-textarea
            v-model="catering"
            label="Ellátás"
            variant="outlined"
            rows="2"
            :counter="cateringMaxLength"
            :maxlength="cateringMaxLength"
          />
        </v-card-text>
        <v-card-text>
          <v-text-field v-model="comment" label="Komment" variant="outlined" />
        </v-card-text>
      </div>

      <v-card-actions>
        <v-spacer />

        <v-btn variant="text" @click="closeDialog"> Mégse </v-btn>

        <v-btn
          color="primary"
          :disabled="!isFormValid || submitting"
          :loading="submitting"
          @click="submit"
        >
          {{ submitLabel }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  computed,
  onMounted,
  watch,
  PropType,
} from "vue";
import Datepicker from "@vuepic/vue-datepicker";
import { AccommodationInquiriesService } from "@/services/AccommodationInquiriesService";
import {
  CostCenterService,
  type CostCenterDTO,
} from "@/services/CostCenterService";
import { useToast } from "@/services/useToast";
import { classifyApiError } from "@/services/useApiError";

/**
 * One form for raising an inquiry and for editing it afterwards. The two differ only in what
 * they start from and where they submit -- keeping them apart would mean keeping two copies of
 * the date rules, and they would drift.
 */
export default defineComponent({
  name: "CustomerAccommodationInquiryFormDialog",
  components: { Datepicker },

  props: {
    show: {
      type: Boolean,
      required: true,
    },
    mode: {
      type: String as PropType<"create" | "edit">,
      default: "create",
    },
    /** The inquiry being edited, as returned by the details endpoint. Ignored when creating. */
    inquiry: {
      type: Object as PropType<any>,
      default: null,
    },
  },
  emits: [
    "closeCreateInquiry",
    "inquiryCreated",
    "inquiryUpdated",
    "inquiryConflict",
  ],

  setup(props, { emit }) {
    const area = ref("");
    const from = ref<Date | string>("");
    const to = ref<Date | string>("");
    const numberOfPeople = ref("");
    const comment = ref("");
    const placementType = ref<string | null>(null);
    const contactName = ref("");
    const contactPhone = ref("");
    const catering = ref("");
    const cateringMaxLength = 1000;
    const placementTypeOptions = [
      { label: "Egyágyas szoba", value: "SINGLE_ROOM" },
      { label: "Kétágyas szoba", value: "DOUBLE_ROOM" },
      { label: "Egyéb", value: "OTHER" },
    ];
    const costCenters = ref<CostCenterDTO[]>([]);
    const selectedCostCenter = ref<number | null>(null);
    const costCenterService = new CostCenterService();
    const { showToast } = useToast();

    const isEdit = computed(() => props.mode === "edit");
    const title = computed(() =>
      isEdit.value ? "Szállásigény módosítása" : "Új szállásigény"
    );
    const submitLabel = computed(() => (isEdit.value ? "Mentés" : "Küldés"));
    const hasSentOffers = computed(() =>
      (props.inquiry?.offerEntityDTOS ?? []).some(
        (offer: any) => offer.status === "PENDING"
      )
    );

    const rules = {
      required: (v: any) => !!v || "Kötelező mező",
      minOne: (v: any) =>
        (v !== "" && Number(v) >= 1) || "Legalább 1 fő szükséges",
    };

    const fromError = computed(() => {
      if (!from.value) return "Kötelező mező";
      return "";
    });

    const toError = computed(() => {
      if (!to.value) return "Kötelező mező";
      if (from.value && to.value) {
        const f = new Date(from.value);
        const t = new Date(to.value);
        if (t <= f) return "A távozás dátuma az érkezés után kell legyen";
        const diffMs = t.getTime() - f.getTime();
        const diffDays = diffMs / (1000 * 60 * 60 * 24);
        if (diffDays < 1) return "Legalább 1 éjszaka szükséges";
      }
      return "";
    });

    const isFormValid = computed(() => {
      return (
        !!area.value &&
        !!from.value &&
        !!to.value &&
        !!numberOfPeople.value &&
        Number(numberOfPeople.value) >= 1 &&
        !!selectedCostCenter.value &&
        !!placementType.value &&
        !!contactName.value &&
        !!contactPhone.value &&
        !fromError.value &&
        !toError.value
      );
    });

    const submitting = ref(false);
    const accommodationInquiriesService = new AccommodationInquiriesService(
      "/inquiry"
    );

    function resetForm() {
      area.value = "";
      from.value = "";
      to.value = "";
      numberOfPeople.value = "";
      comment.value = "";
      placementType.value = null;
      contactName.value = "";
      contactPhone.value = "";
      catering.value = "";
      selectedCostCenter.value = null;
    }

    /** The details endpoint sends dates as yyyy-MM-dd; the picker wants Date objects. */
    function fillFrom(inquiry: any) {
      if (!inquiry) return;
      area.value = inquiry.area ?? "";
      from.value = inquiry.from ? new Date(inquiry.from) : "";
      to.value = inquiry.to ? new Date(inquiry.to) : "";
      numberOfPeople.value =
        inquiry.numberOfPeople === null || inquiry.numberOfPeople === undefined
          ? ""
          : String(inquiry.numberOfPeople);
      comment.value = inquiry.comment ?? "";
      placementType.value = inquiry.placementType ?? null;
      contactName.value = inquiry.contactName ?? "";
      contactPhone.value = inquiry.contactPhone ?? "";
      catering.value = inquiry.catering ?? "";
      selectedCostCenter.value = inquiry.costCenterId ?? null;
    }

    // The dialog is kept mounted by its parent, so it is opening -- not mounting -- that has to
    // load the inquiry into the fields; otherwise a second edit shows the first one's values.
    watch(
      () => [props.show, props.inquiry],
      ([visible]) => {
        if (!visible) return;
        if (isEdit.value) {
          fillFrom(props.inquiry);
        }
      },
      { immediate: true }
    );

    function closeDialog() {
      if (!isEdit.value) {
        resetForm();
      }
      emit("closeCreateInquiry");
    }

    async function loadCostCenters() {
      try {
        const response = await costCenterService.getAll();
        costCenters.value = response;
      } catch (e) {
        console.error(e);
      }
    }

    function payload() {
      return {
        area: area.value,
        from: from.value,
        to: to.value,
        numberOfPeople: numberOfPeople.value,
        comment: comment.value,
        costCenterId: selectedCostCenter.value,
        placementType: placementType.value,
        contactName: contactName.value,
        contactPhone: contactPhone.value,
        catering: catering.value || null,
      };
    }

    async function submit() {
      if (submitting.value) return;
      submitting.value = true;
      try {
        if (isEdit.value) {
          await accommodationInquiriesService.updateAccommodationInquiry(
            String(props.inquiry.id),
            payload()
          );
          emit("inquiryUpdated");
          showToast("Szállásigény módosítva");
        } else {
          await accommodationInquiriesService.createAccommodationInquiry(
            payload()
          );
          // The parent only hides the dialog; without this the next new inquiry opens on the
          // last one's values.
          resetForm();
          emit("inquiryCreated");
          showToast("Szállásigény létrehozva");
        }
      } catch (e) {
        handleSubmitError(e);
      } finally {
        submitting.value = false;
      }
    }

    /**
     * A 409 means the inquiry closed under the user while the dialog was open. Saying so is not
     * enough: the screen behind is stale too, so the parent is told to reload it.
     */
    function handleSubmitError(error: unknown) {
      if (!isEdit.value) {
        showToast("Szállásigény létrehozása sikertelen", "error");
        return;
      }
      const classified = classifyApiError(error);
      if (classified.status === 409) {
        showToast("Az igény időközben lezárult, már nem módosítható", "error");
        emit("inquiryConflict");
        return;
      }
      if (classified.kind === "validation") {
        showToast("A megadott adatok nem érvényesek", "error");
        return;
      }
      showToast("Szállásigény módosítása sikertelen", "error");
    }

    onMounted(() => {
      loadCostCenters();
    });

    return {
      area,
      from,
      to,
      numberOfPeople,
      comment,
      placementType,
      placementTypeOptions,
      contactName,
      contactPhone,
      catering,
      cateringMaxLength,
      closeDialog,
      submit,
      isFormValid,
      submitting,
      selectedCostCenter,
      costCenters,
      rules,
      fromError,
      toError,
      isEdit,
      title,
      submitLabel,
      hasSentOffers,
    };
  },
});
</script>

<style scoped>
/*
 * The dialog card is a flex column with a capped height, so without this the fields simply
 * overflow it and every sibling -- the warning above them included -- is shrunk to make room.
 * Scrolling the fields alone also keeps the warning on screen while the user edits, which is
 * exactly when it is worth reading.
 */
.dialog-fields {
  flex: 1 1 auto;
  /* Without min-height a flex item refuses to shrink below its content, so this would grow
     instead of scrolling -- and the card would go back to squeezing its siblings. */
  min-height: 0;
  overflow-y: auto;
}
</style>
